/**
 * IndexedDB cache for static entity data (cameras, sensors, geofences, etc.)
 *
 * On load: read from cache → show instantly → fetch API in background → update if changed.
 * Reduces initial load from ~6s to ~100ms for cached resources.
 *
 * DB: 'kalan-map-v2-cache'
 * Store: 'entities' — key: entityType, value: { data, timestamp, count }
 */

var DB_NAME = 'kalan-map-v2-cache'
var DB_VERSION = 1
var STORE_NAME = 'entities'
var MAX_AGE_MS = 10 * 60 * 1000 // 10 minutes TTL

var dbPromise = null

function openDB() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise(function(resolve, reject) {
    var request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = function(event) {
      var db = event.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }

    request.onsuccess = function(event) {
      resolve(event.target.result)
    }

    request.onerror = function() {
      console.warn('[entity-cache] IndexedDB failed to open')
      reject(request.error)
    }
  })

  return dbPromise
}

/**
 * Get cached data for an entity type.
 * Returns null if not cached or expired.
 *
 * @param {string} key - entity type or cache key
 * @returns {Promise<Array|null>}
 */
export function getCached(key) {
  return openDB().then(function(db) {
    return new Promise(function(resolve) {
      var tx = db.transaction(STORE_NAME, 'readonly')
      var store = tx.objectStore(STORE_NAME)
      var request = store.get(key)

      request.onsuccess = function() {
        var entry = request.result
        if (!entry) { resolve(null); return }

        var age = Date.now() - entry.timestamp
        if (age > MAX_AGE_MS) {
          resolve(null) // expired
          return
        }

        resolve(entry.data)
      }

      request.onerror = function() { resolve(null) }
    })
  }).catch(function() { return null })
}

/**
 * Store data in cache.
 *
 * @param {string} key
 * @param {Array} data
 * @returns {Promise<void>}
 */
export function setCache(key, data) {
  return openDB().then(function(db) {
    return new Promise(function(resolve) {
      var tx = db.transaction(STORE_NAME, 'readwrite')
      var store = tx.objectStore(STORE_NAME)
      store.put({
        data: data,
        timestamp: Date.now(),
        count: data.length,
      }, key)

      tx.oncomplete = function() { resolve() }
      tx.onerror = function() { resolve() }
    })
  }).catch(function() {})
}

/**
 * Clear all cached data.
 */
export function clearCache() {
  return openDB().then(function(db) {
    return new Promise(function(resolve) {
      var tx = db.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).clear()
      tx.oncomplete = function() { resolve() }
      tx.onerror = function() { resolve() }
    })
  }).catch(function() {})
}
